import { useState } from 'react';
import Layout from '../../components/layout/Layout';
import { Box, Typography, Paper, Tabs, Tab, Divider, List, ListItemButton, ListItemIcon, ListItemText, Chip, useMediaQuery } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import NotificationsIcon from '@mui/icons-material/Notifications';
import EmailIcon from '@mui/icons-material/Email';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import ChatIcon from '@mui/icons-material/Chat';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import QboSettings from '../setting/QboSettings';
import EasyPostSettings from './EasyPostSettings';
import ContractSettings from './ContractSettings';
import ContractPdfSettings from './ContractPdfSettings';
import GeneralSettings from './GeneralSettings';
import NotificationSettings from './NotificationSettings';
import EmailTemplateSettings from './EmailTemplateSettings';
import ContactSettings from './ContactSettings';
import ChatSettings from './ChatSettings';
import TodoSettings from './TodoSettings';
import ChecklistIcon from '@mui/icons-material/FactCheck';

function AdminSettings({ user }) {
  const [activeTab, setActiveTab] = useState(0);
  const isMobile = useMediaQuery('(max-width:900px)');

  const tabs = [
    { label: 'General', icon: <SettingsIcon />, group: 'Core' },
    { label: 'Contracts', icon: <DescriptionIcon />, group: 'Core' },
    { label: '계약서 PDF', icon: <PictureAsPdfIcon />, group: 'Core' },
    { label: 'QuickBooks', icon: <AccountBalanceIcon />, group: 'Finance' },
    { label: 'Shipping', icon: <LocalShippingIcon />, group: 'Finance' },
    { label: 'Notifications', icon: <NotificationsIcon />, group: 'Comms' },
    { label: 'Email Templates', icon: <EmailIcon />, group: 'Comms' },
    { label: 'Contact', icon: <ContactMailIcon />, group: 'Comms' },
    { label: 'Chat', icon: <ChatIcon />, group: 'Comms' },
    { label: 'TODO', icon: <ChecklistIcon />, group: 'Dev' },
  ];
  const groups = ['Core', 'Finance', 'Comms', 'Dev'];

  return (
    <Layout title="Settings" user={user}>
      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          px: { xs: 0.5, sm: 1.5, md: 2 },
          py: { xs: 1, sm: 1.5, md: 2 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 1.5, sm: 2 }, flexWrap: 'wrap' }}>
          <SettingsIcon sx={{ color: '#551FFF', fontSize: { xs: 22, sm: 24 } }} />
          <Typography sx={{ fontSize: { xs: 17, sm: 20 }, fontWeight: 700 }}>Admin Settings</Typography>
        </Box>

        <Paper
          sx={{
            borderRadius: { xs: 2, sm: 3 },
            overflow: 'hidden',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
          }}
        >
          {isMobile ? (
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                borderBottom: '1px solid #e5e7eb',
                px: 0.5,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: { xs: 11, sm: 12 },
                  minHeight: 48,
                  py: 1,
                },
                '& .MuiTab-iconWrapper': { mb: 0.25 },
              }}
            >
              {tabs.map((t, i) => (
                <Tab key={i} label={t.label} icon={t.icon} iconPosition="top" />
              ))}
            </Tabs>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(200px, 260px) minmax(0, 1fr)',
                minHeight: { md: 'min(72vh, 800px)' },
                width: '100%',
                minWidth: 0,
                alignItems: 'stretch',
              }}
            >
              <Box sx={{ borderRight: '1px solid #e5e7eb', p: 1.5, background: '#fcfcff', minWidth: 0 }}>
                {groups.map((group, idx) => (
                  <Box key={group} sx={{ mb: idx === groups.length - 1 ? 0 : 1.5 }}>
                    <Typography sx={{ px: 1, py: 0.6, fontSize: 11, fontWeight: 700, color: '#777' }}>
                      {group}
                    </Typography>
                    <List dense sx={{ p: 0 }}>
                      {tabs.map((tab, i) => tab.group === group ? (
                        <ListItemButton
                          key={tab.label}
                          selected={activeTab === i}
                          onClick={() => setActiveTab(i)}
                          sx={{ borderRadius: 1.5, mb: 0.4 }}
                        >
                          <ListItemIcon sx={{ minWidth: 32, color: activeTab === i ? '#551FFF' : '#777' }}>
                            {tab.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={tab.label}
                            primaryTypographyProps={{ fontSize: 13, fontWeight: activeTab === i ? 700 : 500 }}
                          />
                          {tab.label === 'Contracts' && <Chip label="핵심" size="small" sx={{ height: 18, fontSize: 10 }} />}
                        </ListItemButton>
                      ) : null)}
                    </List>
                    {idx < groups.length - 1 && <Divider sx={{ mt: 1 }} />}
                  </Box>
                ))}
              </Box>
              <Box sx={{ p: { xs: 2, md: 3 }, minWidth: 0, overflow: 'hidden' }}>
                {activeTab === 0 && <GeneralSettings />}
                {activeTab === 1 && <ContractSettings />}
                {activeTab === 2 && <ContractPdfSettings />}
                {activeTab === 3 && <QboSettings />}
                {activeTab === 4 && <EasyPostSettings />}
                {activeTab === 5 && <NotificationSettings />}
                {activeTab === 6 && <EmailTemplateSettings />}
                {activeTab === 7 && <ContactSettings />}
                {activeTab === 8 && <ChatSettings />}
                {activeTab === 9 && <TodoSettings />}
              </Box>
            </Box>
          )}
          {isMobile && (
            <Box sx={{ p: { xs: 1.5, sm: 2 }, minWidth: 0, overflowX: 'hidden' }}>
              {activeTab === 0 && <GeneralSettings />}
              {activeTab === 1 && <ContractSettings />}
              {activeTab === 2 && <ContractPdfSettings />}
              {activeTab === 3 && <QboSettings />}
              {activeTab === 4 && <EasyPostSettings />}
              {activeTab === 5 && <NotificationSettings />}
              {activeTab === 6 && <EmailTemplateSettings />}
              {activeTab === 7 && <ContactSettings />}
              {activeTab === 8 && <ChatSettings />}
              {activeTab === 9 && <TodoSettings />}
            </Box>
          )}
        </Paper>
      </Box>
    </Layout>
  );
}

export default AdminSettings;
